/**
 * Typed fetch wrapper for the GardEn FastAPI backend.
 *
 * Per STORY-001-009 Implementation logic step 1:
 *   - Reads access_token from `auth-store` (in JS memory only —
 *     REQ-PRD-§11.2-A; NEVER localStorage).
 *   - On 401, attempts ONE refresh (POST /auth/refresh) and retries
 *     the original. On refresh failure, clears auth and redirects
 *     to `/login` with a ?return_to=<current> hint.
 *   - Generates an `Idempotency-Key` UUIDv7 header on every mutating
 *     call (POST/PUT/PATCH/DELETE) per REQ-ADD-§6.3-A.
 *   - Parses RFC 9457 problem-details responses (Content-Type
 *     `application/problem+json`) into a typed `ApiError`.
 *
 * Cookies:
 *   `credentials: 'include'` is set for every request so the refresh
 *   cookie rides the same origin. The backend's CORS policy is
 *   responsible for permitting credentialled requests; in dev the
 *   Vite proxy stitches `/auth/*` and `/me*` to the API. A guest's
 *   refresh cookie (`garden_gst`, Path `…/auth/guest`) rides the same
 *   `credentials: 'include'`.
 *
 * Why a hand-rolled fetch wrapper rather than axios:
 *   axios pulls in a non-trivial bundle and an interceptor model
 *   that fights with TanStack Query's mental model. A 70-line
 *   `apiFetch` is easier to reason about and aligns with the rest
 *   of the typed-domain pattern (Pydantic v2 on the server,
 *   discriminated-union responses on the client).
 *
 * Test seam:
 *   Every default behaviour below is overridable per-call via the
 *   options bag. `apiFetch.__resetForTests()` resets the
 *   single-flight refresh promise between unit tests.
 */
import { useAuthStore } from "@/lib/auth-store";
import { queryClient } from "@/lib/queryClient";
import { parseGuestAllowance } from "@/features/chat/guest/allowance";
import {
  clearGuestPointer,
  noteGuestSessionExpired,
  readGuestPointer,
  updateGuestPointer,
} from "@/features/chat/guest/pointer";
import { withGuestSessionLock } from "@/features/chat/guest/session-lock";

/**
 * Discriminated mutating-method type so the Idempotency-Key gate is
 * a compile-time discriminator, not a runtime string match.
 */
export type MutatingMethod = "POST" | "PUT" | "PATCH" | "DELETE";
export type ReadMethod = "GET" | "HEAD" | "OPTIONS";
export type HttpMethod = MutatingMethod | ReadMethod;

const MUTATING_METHODS: ReadonlySet<HttpMethod> = new Set<HttpMethod>([
  "POST",
  "PUT",
  "PATCH",
  "DELETE",
]);

function isMutating(method: HttpMethod): method is MutatingMethod {
  return MUTATING_METHODS.has(method);
}

/**
 * RFC 9457 problem-details body shape, mirroring
 * `apps/api/garden/common/errors.py::_problem_body`.
 */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status: number;
  detail?: string;
  /**
   * Machine-readable error slug — the frontend's localised-error
   * mapping keys off this. Example values: `auth.invalid_credentials`,
   * `password.breached`, `auth.signup.email_exists`,
   * `auth.password.reset.invalid_or_expired`.
   */
  code?: string;
  request_id?: string;
  /**
   * The object form of `detail` — FastAPI's `HTTPException(detail={...})`, the
   * shape the guest contract and the chat limiter use (`{"detail": {"code":
   * …}}`). Kept here because `detail` itself is the RFC 9457 string.
   */
  detail_object?: Record<string, unknown>;
  /**
   * Some endpoints attach extra free-form context (e.g. a
   * lockout's retry-after). Kept loose so the client can pull
   * fields out where needed without a wider type explosion.
   */
  [key: string]: unknown;
}

/**
 * Strongly-typed error thrown by `apiFetch` on every non-2xx response.
 * Callers `instanceof`-check against `ApiError` and switch on `.code`
 * for the localised-error mapping.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly problem: ProblemDetails;
  readonly retryAfterSeconds: number | undefined;

  constructor(problem: ProblemDetails, retryAfterSeconds: number | undefined) {
    super(problem.detail || problem.title || problem.code || "API error");
    this.name = "ApiError";
    this.status = problem.status;
    this.code = problem.code;
    this.problem = problem;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Network error (DNS, TLS, offline). Distinct from `ApiError` so
 * callers can show a "no connection" toast vs. a localised server
 * error.
 */
export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NetworkError";
  }
}

/**
 * UUIDv7 — time-ordered, 128-bit, k-sortable. Per REQ-ADD-§6.3-A
 * the Idempotency-Key SHOULD be unique-per-attempt; UUIDv7 gives
 * us monotonicity without server coordination, which makes log
 * sorting much friendlier.
 *
 * Implementation: 48-bit Unix epoch ms (big-endian) + 4-bit version
 * (`0111`) + 12-bit random + 2-bit variant (`10`) + 62-bit random.
 * Pulls 10 bytes of entropy from `crypto.getRandomValues`. Falls
 * back to Math.random() ONLY in non-browser test contexts where
 * `crypto` is absent (jsdom provides Web Crypto by default).
 */
export function generateUuidV7(now: number = Date.now()): string {
  const bytes = new Uint8Array(16);
  // 48-bit timestamp (ms since epoch).
  const ms = BigInt(now);
  bytes[0] = Number((ms >> 40n) & 0xffn);
  bytes[1] = Number((ms >> 32n) & 0xffn);
  bytes[2] = Number((ms >> 24n) & 0xffn);
  bytes[3] = Number((ms >> 16n) & 0xffn);
  bytes[4] = Number((ms >> 8n) & 0xffn);
  bytes[5] = Number(ms & 0xffn);

  // 10 bytes of randomness for bytes[6..15].
  const random = new Uint8Array(10);
  const cryptoApi =
    typeof globalThis !== "undefined" &&
    (globalThis as { crypto?: Crypto }).crypto &&
    typeof (globalThis as { crypto?: Crypto }).crypto?.getRandomValues ===
      "function"
      ? (globalThis as { crypto: Crypto }).crypto
      : null;
  if (cryptoApi !== null) {
    cryptoApi.getRandomValues(random);
  } else {
    // Fallback for environments without Web Crypto. UUIDv7 still works
    // for idempotency purposes; collisions are vanishingly unlikely.
    for (let i = 0; i < random.length; i++) {
      random[i] = Math.floor(Math.random() * 256);
    }
  }
  for (let i = 0; i < 10; i++) {
    bytes[6 + i] = random[i] ?? 0;
  }

  // Set version (bits 48..51) to 0111 (UUIDv7).
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x70;
  // Set variant (bits 64..65) to 10.
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
  return (
    hex.slice(0, 4).join("") +
    "-" +
    hex.slice(4, 6).join("") +
    "-" +
    hex.slice(6, 8).join("") +
    "-" +
    hex.slice(8, 10).join("") +
    "-" +
    hex.slice(10, 16).join("")
  );
}

/**
 * Module-level state for the single-flight refresh. If many requests
 * race a 401 simultaneously, they share one refresh attempt — the
 * first response unblocks the rest.
 */
/**
 * What a refresh attempt actually established.
 *
 * The distinction is load-bearing: `attemptRefresh` used to answer `false` for
 * both "the server rejected the cookie" and "the request never arrived", and
 * every caller reads a negative answer as "the session is over" — so a dropped
 * connection or a 502 during a deploy signed out users whose refresh cookie
 * was still valid (2026-08-13). Only `session-ended` may clear auth.
 */
export type RefreshOutcome = "refreshed" | "session-ended" | "unavailable";

let inFlightRefresh: Promise<RefreshOutcome> | null = null;
let inFlightGuestRefresh: Promise<RefreshOutcome> | null = null;

/**
 * Module-level config so test setups can rewire the base URL + the
 * redirect-on-persistent-401 callback without monkey-patching
 * react-router. The default redirect uses `window.location.assign`
 * which both the app and tests can spy on.
 */
export interface ApiClientConfig {
  baseUrl: string;
  /**
   * Called when the client gives up after a persistent 401. The
   * caller should clear in-memory auth and redirect to `/login`.
   * The current pathname + search is passed so the caller can
   * attach a `return_to` hint.
   */
  onUnauthorizedRedirect: (currentPathWithSearch: string) => void;
  /**
   * Called when a MEMBER session ends without an explicit logout (a member
   * token's persistent 401, W0.3): clears client state that must not outlive
   * the session — the chat store, and since W2.6 this browser's guest side
   * (pointer, draft, guest token, a pending claim, the server-side discard).
   * Never called for a request that carried no token at all: there was no
   * session to end (W2.6, W2.1 carry). Injected (main.tsx) rather than
   * imported, because `features/chat/chat-store.ts` is not one of the leaf
   * modules the layering rule below carves out, and importing it here would
   * risk a cycle.
   *
   * Layering rule (W2.1, controller ruling P3): this file MAY import the LEAF
   * guest modules — `features/chat/guest/allowance.ts`, `pointer.ts`,
   * `session-lock.ts` — directly, because none of them imports api-client.
   * It must NEVER import `features/chat/guest/api.ts`, which itself imports
   * api-client; doing so would create an import cycle. Anything else that
   * needs feature-level state stays injected through this config instead,
   * like `onSessionEnded` here.
   */
  onSessionEnded: () => void;
}

const DEFAULT_CONFIG: ApiClientConfig = {
  // Relative base. PROD (mode=production): CloudFront serves the API same-origin
  // → "". DEV (mode=development): default to "/api", which the Vite proxy forwards
  // to :8000 (see vite.config.ts) — avoids colliding with SPA page routes (/chat,
  // /auth/handoff, /admin). TEST (mode=test): "". An optional VITE_API_BASE_URL
  // env overrides all three. No committed .env file required.
  baseUrl:
    import.meta.env.VITE_API_BASE_URL ??
    (import.meta.env.MODE === "development" ? "/api" : ""),
  onUnauthorizedRedirect: (returnTo) => {
    if (typeof window !== "undefined") {
      const url = "/login?return_to=" + encodeURIComponent(returnTo);
      window.location.assign(url);
    }
  },
  onSessionEnded: () => undefined,
};

let currentConfig: ApiClientConfig = { ...DEFAULT_CONFIG };

export function configureApiClient(patch: Partial<ApiClientConfig>): void {
  currentConfig = { ...currentConfig, ...patch };
}

export function getApiClientConfig(): Readonly<ApiClientConfig> {
  return currentConfig;
}

/**
 * Reset module-level state — for use in test setups only.
 */
export function __resetApiClientForTests(): void {
  inFlightRefresh = null;
  inFlightGuestRefresh = null;
  currentConfig = { ...DEFAULT_CONFIG };
}

export interface ApiFetchOptions<TBody> {
  method: HttpMethod;
  path: string;
  body?: TBody;
  /**
   * Whether to skip the 401 → refresh → retry dance. Set to `true`
   * for endpoints that should NEVER trigger a refresh (e.g. the
   * refresh endpoint itself, the login endpoint).
   */
  skipRefreshOn401?: boolean;
  /**
   * Override the `Idempotency-Key` for this call. Useful when the
   * caller wants the SERVER to dedupe across retries (e.g. when an
   * OTP form is re-submitted). The default generates a fresh
   * UUIDv7 per call.
   */
  idempotencyKey?: string;
  /**
   * Additional headers. The wrapper sets `Content-Type` and
   * `Idempotency-Key` itself; anything here is merged on top
   * (and the caller's value wins).
   */
  headers?: Record<string, string>;
  /**
   * AbortSignal for cancellation — wired straight through to fetch.
   */
  signal?: AbortSignal;
  /**
   * Guest chat (SCRUM-273): this route also accepts a guest token
   * (`current_principal_or_guest` server-side). With no member token in
   * memory the guest token is attached instead, and a 401 runs the
   * single-flight GUEST refresh — never the member refresh-and-redirect: a
   * background call must not send a guest to /login. A member call is
   * unchanged.
   */
  allowGuest?: boolean;
}

const REFRESH_PATH = "/auth/refresh";

/**
 * Run a single-flight refresh. Subsequent calls during the same
 * refresh attempt share the same promise. Returns true on success
 * (new access token stored in `auth-store`); false otherwise.
 *
 * EXPORTED because it must be the ONLY refresher in the tab. Refresh tokens
 * are single-use and rotating, and the server treats a second presentation of
 * a consumed token as replay: it revokes the whole family, killing the session
 * for the winner too (reproduced against the API 2026-08-13 — concurrent POSTs
 * gave 200 + 401 `auth.refresh.family_invalid`, and the 200's fresh token was
 * then dead). Any code path that needs a token refresh — including the SSE
 * stream, which cannot use `apiFetch` because that wrapper parses the body as
 * JSON — MUST call this rather than POST /auth/refresh itself.
 */
export async function attemptRefresh(): Promise<RefreshOutcome> {
  if (inFlightRefresh !== null) {
    return inFlightRefresh;
  }
  inFlightRefresh = (async () => {
    try {
      const url = currentConfig.baseUrl + REFRESH_PATH;
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "Idempotency-Key": generateUuidV7(),
      };
      const res = await fetch(url, {
        method: "POST",
        headers,
        credentials: "include",
        body: "{}",
      });
      if (!res.ok) {
        // ONLY the server saying "this session is over" ends a session.
        // A 5xx (API rolling, proxy hiccup) says nothing about the cookie.
        return res.status === 401 || res.status === 403
          ? "session-ended"
          : "unavailable";
      }
      const payload = (await res.json()) as {
        access_token?: string;
        token_type?: string;
        expires_in?: number;
      };
      if (typeof payload.access_token !== "string") {
        // 200 without a token is a broken response, not a dead session.
        return "unavailable";
      }
      useAuthStore.getState().setAccessToken(payload.access_token);
      return "refreshed";
    } catch {
      // The request never got an answer — offline, DNS, TLS, abort. Treating
      // this as a dead session is what signed users out on a wifi blip.
      return "unavailable";
    } finally {
      // Always release the single-flight slot so the next 401 can
      // trigger a fresh attempt.
      inFlightRefresh = null;
    }
  })();
  return inFlightRefresh;
}

/**
 * The guest twin of `attemptRefresh` (contract: POST /auth/guest/refresh with
 * the `garden_gst` cookie). Single-flight in this tab AND serialised across
 * tabs by the guest session lock — guest refresh tokens rotate and a second
 * presentation of a consumed one revokes the family. EVERY 401
 * (`auth.refresh.missing` / `auth.refresh.invalid` — contract amendment B-5)
 * ends the guest session locally (token + pointer) and notes "expired" when a
 * session was live. Anything else that is not a 200 — 503 `guest.unavailable`
 * while Redis is down (B-3), another 5xx, a 403, offline — is `unavailable`
 * and keeps the session: only the server saying "this session is over" ends
 * one.
 *
 * A 200 that lands after the session moved on stores nothing it would bring
 * back (final web fix round, item 3; the same guard as ChatRoute's
 * /auth/guest/me sync): the pointer is updated only while it still exists,
 * and the guest token is stored only while no member token is in memory and
 * the pointer this refresh started with has not been cleared meanwhile — a
 * claim, a discard or another tab's session end removed it on purpose. A
 * browser that never had a pointer (storage blocked) still gets its token.
 * The outcome stays "refreshed": the server did refresh, and a caller's retry
 * then reads whatever identity this tab holds now.
 */
export function attemptGuestRefresh(): Promise<RefreshOutcome> {
  if (inFlightGuestRefresh !== null) {
    return inFlightGuestRefresh;
  }
  inFlightGuestRefresh = withGuestSessionLock(async (): Promise<RefreshOutcome> => {
    const hadPointer = readGuestPointer() !== null;
    try {
      const res = await fetch(currentConfig.baseUrl + AUTH_PATHS.guestRefresh, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Idempotency-Key": generateUuidV7(),
        },
        credentials: "include",
        body: "{}",
      });
      if (!res.ok) {
        // B-5: every 401 is the server ending the session; B-3: a 503
        // `guest.unavailable` (Redis down) and every other failure keep it.
        if (res.status === 401) {
          endGuestSessionLocally();
          return "session-ended";
        }
        return "unavailable";
      }
      const payload = (await res.json()) as {
        access_token?: unknown;
        allowance?: unknown;
      };
      if (typeof payload.access_token !== "string") {
        return "unavailable";
      }
      const pointerNow = readGuestPointer() !== null;
      const memberNow = useAuthStore.getState().accessToken !== null;
      if (!memberNow && (pointerNow || !hadPointer)) {
        useAuthStore.getState().setGuestToken(payload.access_token);
      }
      if (pointerNow) {
        const allowance = parseGuestAllowance(payload.allowance);
        updateGuestPointer({
          hasSession: true,
          ...(allowance !== null ? { allowance } : {}),
        });
      }
      return "refreshed";
    } catch {
      return "unavailable";
    }
  }).finally(() => {
    inFlightGuestRefresh = null;
  });
  return inFlightGuestRefresh;
}

/** The server ended this guest session (expired, claimed elsewhere, discarded). */
function endGuestSessionLocally(): void {
  useAuthStore.getState().clearGuest();
  if (readGuestPointer()?.hasSession === true) {
    noteGuestSessionExpired();
  }
  clearGuestPointer();
}

/**
 * One-shot boot-time silent refresh. Call once at app startup (main.tsx).
 *
 * The access token lives in JS memory only (REQ-PRD-§11.2-A), so a hard
 * reload / direct-link starts with `accessToken: null` even when the user
 * has a valid HttpOnly `garden_rt` refresh cookie. This attempts a single
 * refresh (which sets the access token in `auth-store` on success), then
 * flips the store's `bootstrapped` flag so route guards stop showing their
 * pending state. Never throws: `attemptRefresh` swallows its own errors and
 * returns a boolean, and the `bootstrapped` flag is set unconditionally so a
 * guard can't hang if the refresh fails (no cookie / network error).
 */
export async function bootstrapAuth(): Promise<void> {
  try {
    const outcome = await attemptRefresh();
    // Guest chat (contract): only after the server said "no member session" —
    // never on `unavailable`, which says nothing about either cookie — and
    // only when this browser started a guest conversation.
    if (outcome === "session-ended" && readGuestPointer()?.hasSession === true) {
      await attemptGuestRefresh();
    }
  } finally {
    useAuthStore.getState().setBootstrapped();
  }
}

function currentPathWithSearch(): string {
  if (typeof window === "undefined") {
    return "/";
  }
  return window.location.pathname + window.location.search;
}

async function readProblemBody(res: Response): Promise<ProblemDetails> {
  // Servers SHOULD set `application/problem+json` but some routes
  // (e.g. 422 from FastAPI's default validation) use plain JSON.
  // Try JSON regardless; on failure, fall back to a minimal shape.
  try {
    const raw = (await res.json()) as Record<string, unknown>;
    const status =
      typeof raw["status"] === "number" ? (raw["status"] as number) : res.status;
    const nested =
      typeof raw["detail"] === "object" &&
      raw["detail"] !== null &&
      !Array.isArray(raw["detail"])
        ? (raw["detail"] as Record<string, unknown>)
        : null;
    // C4: a nested `detail.code` (guest codes, chat limiter) is the code when
    // the body has no top-level one, so callers can switch on `ApiError.code`.
    const code =
      typeof raw["code"] === "string"
        ? raw["code"]
        : nested !== null && typeof nested["code"] === "string"
          ? nested["code"]
          : undefined;
    const detail =
      typeof raw["detail"] === "string" ? raw["detail"] : undefined;
    const title = typeof raw["title"] === "string" ? raw["title"] : undefined;
    return {
      ...raw,
      status,
      code,
      detail,
      title,
      ...(nested !== null ? { detail_object: nested } : {}),
    } as ProblemDetails;
  } catch {
    return {
      status: res.status,
      title: res.statusText,
    };
  }
}

function readRetryAfter(res: Response): number | undefined {
  const header = res.headers.get("Retry-After");
  if (header === null) return undefined;
  const seconds = Number.parseInt(header, 10);
  if (!Number.isFinite(seconds) || seconds < 0) return undefined;
  return seconds;
}

/**
 * The core fetch wrapper. Generic over the request body and the
 * response body. Callers narrow the response type at the call site
 * (or import the matching DTO from `packages/shared-types` once the
 * OpenAPI codegen lands — STORY-002-001 etc.).
 */
export async function apiFetch<TResponse, TBody = unknown>(
  options: ApiFetchOptions<TBody>,
): Promise<TResponse> {
  return await apiFetchInternal<TResponse, TBody>(options, /*allowRetry*/ true);
}

async function apiFetchInternal<TResponse, TBody = unknown>(
  options: ApiFetchOptions<TBody>,
  allowRetry: boolean,
): Promise<TResponse> {
  const { method, path, body, signal } = options;
  const skipRefreshOn401 = options.skipRefreshOn401 === true;

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...options.headers,
  };

  // Content-Type only for requests with a JSON body. FormData is the
  // exception: the browser must set `multipart/form-data` itself so it can
  // append the boundary parameter, and setting it here produces a request
  // the server cannot parse. Voice input (POST /chat/transcribe) is the
  // first multipart caller; it routes through this wrapper rather than a
  // bare fetch so it keeps the 401 -> refresh -> retry dance and the
  // Idempotency-Key, both of which a hand-rolled fetch would lose.
  const isFormData =
    typeof FormData !== "undefined" && body instanceof FormData;
  if (body !== undefined && !isFormData) {
    headers["Content-Type"] = headers["Content-Type"] ?? "application/json";
  }

  // Idempotency-Key on every mutating method (REQ-ADD-§6.3-A).
  if (isMutating(method)) {
    headers["Idempotency-Key"] =
      options.idempotencyKey ?? headers["Idempotency-Key"] ?? generateUuidV7();
  }

  // Bearer from the in-memory store. A guest-allowed call with no member token
  // carries the guest token instead (W2.1); everything else is unchanged.
  const { accessToken, guestToken } = useAuthStore.getState();
  const guestCall = options.allowGuest === true && accessToken === null;
  const bearer = guestCall ? guestToken : accessToken;
  if (bearer !== null && headers["Authorization"] === undefined) {
    headers["Authorization"] = "Bearer " + bearer;
  }

  const init: RequestInit = {
    method,
    headers,
    credentials: "include",
    signal,
  };
  if (body !== undefined) {
    init.body = isFormData ? (body as unknown as FormData) : JSON.stringify(body);
  }

  let res: Response;
  try {
    res = await fetch(currentConfig.baseUrl + path, init);
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw err;
    }
    throw new NetworkError("Failed to reach the server.");
  }

  // 2xx — happy path.
  if (res.status >= 200 && res.status < 300) {
    // 204 No Content — return an empty object cast (callers that
    // expect `void` will simply ignore the value).
    if (res.status === 204) {
      return undefined as TResponse;
    }
    // Bodies might be empty even on 200 (rare); guard the JSON parse.
    const text = await res.text();
    if (text.length === 0) {
      return undefined as TResponse;
    }
    try {
      return JSON.parse(text) as TResponse;
    } catch {
      throw new NetworkError("Server returned an invalid JSON body.");
    }
  }

  // 401 on a guest call: the guest refresh, never the member
  // refresh-and-redirect (a guest is never sent to /login by a fetch).
  if (res.status === 401 && guestCall) {
    if (allowRetry && !skipRefreshOn401 && guestToken !== null) {
      const outcome = await attemptGuestRefresh();
      if (outcome === "refreshed") {
        return await apiFetchInternal<TResponse, TBody>(options, false);
      }
    }
    const guestProblem = await readProblemBody(res);
    throw new ApiError(guestProblem, readRetryAfter(res));
  }

  // 401 — try a single refresh + retry.
  if (res.status === 401 && allowRetry && !skipRefreshOn401) {
    // A guest token lives in memory but THIS call didn't use it
    // (`allowGuest` wasn't set, so `guestCall` above is false): there is no
    // member session to refresh. Running the member refresh-and-redirect
    // dance here would wipe the guest's chat store/cache and send them to
    // /login over a 401 from some unrelated call (hardening, fix round 1). A
    // true member (a member token was sent) and a true anonymous visitor
    // (neither token in memory) keep today's behaviour exactly — only this
    // "logged out of nothing" guest case is new.
    if (accessToken === null && guestToken !== null) {
      const guestProblem = await readProblemBody(res);
      throw new ApiError(guestProblem, readRetryAfter(res));
    }
    const outcome = await attemptRefresh();
    if (outcome === "refreshed") {
      // Retry once. allowRetry=false prevents a refresh loop if the
      // backend somehow returns 401 again after a fresh token.
      return await apiFetchInternal<TResponse, TBody>(options, false);
    }
    if (outcome === "unavailable") {
      // Could not reach a verdict — surface the error and KEEP the session.
      // The next request will try again; the cookie is untouched.
      const transient = await readProblemBody(res);
      throw new ApiError(transient, readRetryAfter(res));
    }
    // Refresh failed — clear auth and redirect.
    useAuthStore.getState().clearAuth();
    // SCRUM-138: a persistent 401 ends the session the same way an
    // explicit logout does — the cache must not survive it either
    // (whatever account re-logs in next reads stale data otherwise).
    queryClient.clear();
    // W0.3: same reasoning extended to the chat store (+ the W2.6 guest
    // side) — a previous person's open thread must not survive this.
    // W2.6 (W2.1 carry): only when a MEMBER session ended — a member token
    // was sent and refused. A request that carried no token at all ended
    // nothing, and `onSessionEnded` now also discards this browser's guest
    // conversation: a stray member-only call from a tab whose guest token is
    // not in memory yet must never throw that conversation away.
    if (accessToken !== null) currentConfig.onSessionEnded();
    currentConfig.onUnauthorizedRedirect(currentPathWithSearch());
    const problem = await readProblemBody(res);
    throw new ApiError(problem, readRetryAfter(res));
  }

  // Non-2xx, non-recoverable.
  const problem = await readProblemBody(res);
  throw new ApiError(problem, readRetryAfter(res));
}

/**
 * Convenience wrappers — keep the call sites tight.
 */
export const apiClient = {
  get<TResponse>(
    path: string,
    options: Omit<ApiFetchOptions<undefined>, "method" | "path" | "body"> = {},
  ): Promise<TResponse> {
    return apiFetch<TResponse, undefined>({ method: "GET", path, ...options });
  },
  post<TResponse, TBody = unknown>(
    path: string,
    body: TBody,
    options: Omit<ApiFetchOptions<TBody>, "method" | "path" | "body"> = {},
  ): Promise<TResponse> {
    return apiFetch<TResponse, TBody>({ method: "POST", path, body, ...options });
  },
  put<TResponse, TBody = unknown>(
    path: string,
    body: TBody,
    options: Omit<ApiFetchOptions<TBody>, "method" | "path" | "body"> = {},
  ): Promise<TResponse> {
    return apiFetch<TResponse, TBody>({ method: "PUT", path, body, ...options });
  },
  delete<TResponse>(
    path: string,
    options: Omit<ApiFetchOptions<undefined>, "method" | "path" | "body"> = {},
  ): Promise<TResponse> {
    return apiFetch<TResponse, undefined>({
      method: "DELETE",
      path,
      ...options,
    });
  },
};

/**
 * Pinned endpoint paths — server-side constants from
 * `apps/api/garden/auth/router_*.py`. Keep them here as a single
 * source of truth on the client; if any path changes server-side,
 * fix this list AND the matching call site.
 */
export const AUTH_PATHS = {
  signup: "/auth/signup",
  emailVerify: "/auth/email/verify",
  emailResend: "/auth/email/resend",
  login: "/auth/login",
  refresh: "/auth/refresh",
  logout: "/auth/logout",
  passwordResetRequest: "/auth/password/reset/request",
  passwordResetConfirm: "/auth/password/reset/confirm",
  phoneStart: "/auth/phone/start",
  phoneVerify: "/auth/phone/verify",
  oauthGoogleStart: "/auth/oauth/google/start",
  oauthFacebookStart: "/auth/oauth/facebook/start",
  oauthHandoff: "/auth/oauth/handoff",
  guestConfig: "/auth/guest/config",
  guestCreate: "/auth/guest",
  guestRefresh: "/auth/guest/refresh",
  guestMe: "/auth/guest/me",
  guestClaim: "/auth/guest/claim",
  guestDiscard: "/auth/guest/discard",
} as const;
